import { useMemo } from "react";
import type { CanvasObject, ConnectorCanvasObject } from "@refinement-canvas/domain";
import { isObjectPlacementTool, usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useConnectorDragStore, type LiveFrame } from "../store/connector-drag-store";
import { useConnectorRouteDraftStore } from "../store/connector-route-draft-store";
import {
  computeConnectorGeometry,
  connectorGeometryInputFor,
  connectorPathData,
  type ConnectorFrame,
} from "../../../lib/connector-geometry";
import { CONNECTOR_DEFAULT_STROKE } from "../../../lib/connector-style";
import { ConnectorShape } from "./connector-shape";

interface Props {
  readonly object: ConnectorCanvasObject;
}

// クリックしやすいよう、見た目の線より太い透明の当たり判定線を重ねる。
const HIT_STROKE_WIDTH = 14;
// 矢じり・線幅が SVG 枠でクリップされないよう、外接矩形の周囲へ確保する余白。
// （経路の編集の追加後は、外接矩形を共有の幾何が線幅・両端の形状まで含めて求めるため、ここでは
//  当たり判定線・選択中の太い線のぶんに足す小さな余白だけを持つ。）
const SVG_EXTRA_PAD = 2;

// コネクタ（接続矢印）オブジェクト。
// 端点 from / to の接続先オブジェクトの現在位置（ドラッグ中はライブ枠）から都度ルーティングし、
// 直交折れ線 + 矢じりを SVG で描画する。クリックで選択でき、他オブジェクトと同様に削除できる。
// 経路の編集の追加後は、線の形・経路・両端の形状から共有の幾何（computeConnectorGeometry）で描き、
// 経路の編集中は確定前の下書きで描く。当たり判定は破線の隙間でも途切れない。
export function ConnectorObject({ object }: Props): JSX.Element | null {
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);
  const setSelectedObjectIds = useCanvasStore((s) => s.setSelectedObjectIds);

  // 端点オブジェクトを doc store から購読し、移動・リサイズの確定で再ルーティングする。
  const fromObj = useCanvasDocStore((s) => s.objects[object.from.objectId]);
  const toObj = useCanvasDocStore((s) => s.objects[object.to.objectId]);
  // ドラッグ / リサイズ中は確定前のライブ枠を優先し、リアルタイムに追従させる。
  const fromLive = useConnectorDragStore((s) => s.frames[object.from.objectId]);
  const toLive = useConnectorDragStore((s) => s.frames[object.to.objectId]);
  // 経路の編集中（本人のドラッグ・矢印キー）は、確定前の下書きの経路で描く。
  const routeDraft = useConnectorRouteDraftStore((s) =>
    s.draft?.connectorId === object.id ? s.draft : null
  );

  const fromFrame = resolveFrame(fromLive, fromObj);
  const toFrame = resolveFrame(toLive, toObj);
  const source = routeDraft
    ? { ...object, lineType: routeDraft.lineType, route: routeDraft.route }
    : object;
  // 経路の計算（自動経路の探索を含む）は、端点の位置・辺・見た目・経路・太さが変わったときだけ行う
  // （パン・ズームや、ほかの図形の編集による再描画のたびに、すべての接続矢印を計算し直さない）。
  const fromKey = frameKey(fromFrame);
  const toKey = frameKey(toFrame);
  const geometry = useMemo(
    () =>
      fromFrame && toFrame
        ? computeConnectorGeometry(connectorGeometryInputFor(source, fromFrame, toFrame))
        : null,
    // 枠は描画ごとに作り直すため、値の文字列（fromKey・toKey）で比べる（同じ値なら前回の結果を使う）。
    [
      fromKey,
      toKey,
      source.from.side,
      source.to.side,
      source.lineType,
      source.linePattern,
      source.startCap,
      source.endCap,
      source.route,
      source.strokeWidth,
    ]
  );
  // 接続先が削除された等で端点が解決できない場合は何も描画しない（dangling 対策）。
  if (!geometry) return null;

  const effectiveStroke = object.stroke ?? CONNECTOR_DEFAULT_STROKE;
  const effectiveStrokeWidth = geometry.strokeWidth;
  // 選択中も太いコネクタが細く見えないよう、実効幅より 1px 太く、従来の 3px を下回らない。
  const stroke = isSelected ? "var(--color-selection)" : effectiveStroke;
  const strokeWidth = isSelected ? Math.max(effectiveStrokeWidth + 1, 3) : effectiveStrokeWidth;
  const hitStrokeWidth = Math.max(HIT_STROKE_WIDTH, effectiveStrokeWidth + 8);
  // 太い線や矢じりが SVG 枠で切れないよう、描画線幅の半分を余白へ上乗せする。
  // 外接矩形は線幅と両端の形状を含む。当たり判定線と選択中の太い線の分だけ余白を足す。
  const svgPad = Math.ceil(Math.max(hitStrokeWidth, strokeWidth) / 2) + SVG_EXTRA_PAD;
  const originX = geometry.bounds.x - svgPad;
  const originY = geometry.bounds.y - svgPad;
  const width = geometry.bounds.w + svgPad * 2;
  const height = geometry.bounds.h + svgPad * 2;
  // ルート点を SVG ローカル座標へ変換する。
  const toLocal = (point: { readonly x: number; readonly y: number }) => ({
    x: point.x - originX,
    y: point.y - originY,
  });

  const onPointerDown = (event: React.PointerEvent<SVGPathElement>): void => {
    // 付箋 / 図形 / テキスト / スタンプ / コメント配置ツール選択中は、コネクタ上でも新規配置を許す
    // （イベントを止めず stage 側へバブリングさせる）。
    if (isObjectPlacementTool(activeTool)) {
      return;
    }
    event.stopPropagation();
    const selected = useCanvasStore.getState().selectedObjectIds;
    if (!selected.includes(object.id)) {
      setSelectedObjectIds([object.id]);
    }
  };

  return (
    <svg
      data-testid={`canvas-object-${object.id}`}
      data-object-type="connector"
      data-line-type={geometry.lineType}
      data-line-pattern={geometry.linePattern}
      data-start-cap={geometry.startCap?.cap ?? "none"}
      data-end-cap={geometry.endCap?.cap ?? "none"}
      data-route-mode={geometry.manual ? "manual" : "auto"}
      data-route-adjusted={geometry.adjusted ? "true" : undefined}
      style={{
        position: "absolute",
        left: `${originX}px`,
        top: `${originY}px`,
        width: `${width}px`,
        height: `${height}px`,
        zIndex: object.zOrder,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      {/* 当たり判定用の太い透明線（選択しやすくする）。破線でも途切れない全体の経路を使う。 */}
      <path
        data-testid="connector-hit-area"
        d={connectorPathData(geometry.hitPath, toLocal)}
        fill="none"
        stroke="transparent"
        strokeWidth={hitStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          pointerEvents: activeTool === "draw-pen" ? "none" : "stroke",
          // 参照専用（埋め込み）では default。pointer を出してよいのは、利用者が挙げたリンク・
          // リンクカード・コメントピンに限る（コネクタのクリックは選択ハイライトが出るだけで、
          // 閲覧者にとって意味のある遷移や操作は起きない）。配置ツール・ペン描画の選択中は、
          // コネクタ上でも新規作成（crosshair）であることを示す。
          cursor: readOnly ? "default" : usesCrosshairCursor(activeTool) ? "crosshair" : "pointer",
        }}
        onPointerDown={onPointerDown}
      />
      {/* 本体線と両端の形状。 */}
      <ConnectorShape geometry={geometry} color={stroke} strokeWidth={strokeWidth} map={toLocal} />
    </svg>
  );
}

/** 枠の値を比べるための文字列（端点が解決できなければ空文字）。 */
function frameKey(frame: ConnectorFrame | null): string {
  return frame ? `${frame.x},${frame.y},${frame.w},${frame.h},${frame.rotation ?? 0}` : "";
}

// ライブ枠（ドラッグ中の確定前フレーム）があればそれを、なければ doc store のオブジェクト枠を返す。
// 回転済み・回転中の図形にもアンカーを追従させるため、回転角も含めて返す。
function resolveFrame(
  live: LiveFrame | undefined,
  object: CanvasObject | undefined
): ConnectorFrame | null {
  if (live) return { x: live.x, y: live.y, w: live.w, h: live.h, rotation: live.rotation ?? 0 };
  if (object) {
    return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
  }
  return null;
}
